import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare, Patient, Practitioner } from '@medplum/fhirtypes';
import { ADMIN_EXTENSION_URLS } from './adminDirectoryConstants';
import {
  buildCompartmentMetaFromPatientAndPanel,
  collectPatientMetaAccounts,
} from './clientFacingTasks';
import { provisionEpisodePanelViaBot } from '../pages/public/intake/intakeLeadPatient';
import {
  createServiceRequestPair,
  type ServiceRequestPair,
} from './serviceRequestService';
import type { ServiceRequestRequesterReference } from './serviceRequestFhirMapping';

export async function createFulfilledEpisodePair(opts: {
  medplum: MedplumClient;
  patient: Patient;
  therapyTypeCode: string;
  practitionerId: string;
  practitionerDisplay?: string;
  organizationId?: string;
  requester?: ServiceRequestRequesterReference;
}): Promise<ServiceRequestPair> {
  const performerOrganization = opts.organizationId
    ? {
        reference: `Organization/${opts.organizationId}` as const,
        display: undefined,
      }
    : undefined;

  const providerRef = {
    reference: `Practitioner/${opts.practitionerId}`,
    ...(opts.practitionerDisplay ? { display: opts.practitionerDisplay } : {}),
  };

  const pair = await createServiceRequestPair({
    live: true,
    medplum: opts.medplum,
    input: {
      patient: opts.patient,
      therapyTypeCode: opts.therapyTypeCode,
      stage: 'fulfilled',
      performerOrganization,
      requester: opts.requester,
      careManager: providerRef,
    },
  });
  if (!pair?.episodeOfCare.id || !pair.serviceRequest.id) {
    throw new Error('Failed to create Service Request and Episode of Care.');
  }

  const panel = await provisionEpisodePanelViaBot(opts.medplum, {
    patientId: opts.patient.id!,
    practitionerId: opts.practitionerId,
    organizationId: opts.organizationId,
  });

  const [patientAfter, eocAfterPropagate, srAfterPropagate] = await Promise.all([
    opts.medplum.readResource('Patient', opts.patient.id!),
    opts.medplum.readResource('EpisodeOfCare', pair.episodeOfCare.id),
    opts.medplum.readResource('ServiceRequest', pair.serviceRequest.id),
  ]);

  const orgId = opts.organizationId?.trim() ?? organizationIdFromPatient(patientAfter);

  const panelDisplay = panel.careTeamName ?? opts.practitionerDisplay;
  const compartmentMeta = (existing: typeof eocAfterPropagate.meta) =>
    buildCompartmentMetaFromPatientAndPanel(
      patientAfter,
      panel.careTeamId,
      orgId,
      existing,
    );

  const episodePatch: EpisodeOfCare = {
    ...eocAfterPropagate,
    status: 'active',
    careManager: providerRef,
    team: [{
      reference: `CareTeam/${panel.careTeamId}`,
      ...(panelDisplay ? { display: panelDisplay } : {}),
    }],
    meta: compartmentMeta(eocAfterPropagate.meta),
  };
  const episodeOfCare = await opts.medplum.updateResource(episodePatch);

  const serviceRequest = await opts.medplum.updateResource({
    ...srAfterPropagate,
    meta: compartmentMeta(srAfterPropagate.meta),
  });

  return { serviceRequest, episodeOfCare };
}

export function organizationIdFromPractitioner(practitioner: Practitioner): string | undefined {
  const ref = practitioner.extension?.find(
    (e) => e.url === ADMIN_EXTENSION_URLS.organization,
  )?.valueReference?.reference;
  return ref?.startsWith('Organization/') ? ref.replace('Organization/', '') : undefined;
}

export function organizationIdFromPatient(patient: Patient): string | undefined {
  for (const ref of collectPatientMetaAccounts(patient)) {
    if (ref.reference?.startsWith('Organization/')) {
      return ref.reference.slice('Organization/'.length);
    }
  }
  return undefined;
}
